import {
  CustomMappingOption,
  CustomMappingType,
  ICustomMappingSourceOption,
  IFailedCustomMappingRecord,
  IPracticeMigration,
  ISourceEntityTypes,
} from '@principle-theorem/practice-migrations/interfaces';
import { CustomMapping } from '../custom-mapping';
import { AppointmentStatus } from '@principle-theorem/principle-core/interfaces';
import { BaseCustomMappingHandler } from '../base-custom-mapping-handler';
import { BaseSourceEntity } from '../source/base-source-entity';
import {
  asyncForEach,
  Firestore,
  getEnumValues,
  IBlobFilenamePair,
  snapshot,
  WithRef,
  XSLXImporterExporter,
} from '@principle-theorem/shared';
import { compact, sortBy } from 'lodash';
import { of } from 'rxjs';
import { TranslationMapHandler } from '../translation-map';
import { AppointmentBookToStatusToXSLX } from './appointment-book-to-status-to-xlsx';
import { XSLXToAppointmentBookToStatus } from './xlsx-to-appointment-to-status';
import { PracticeMigration } from '../practice-migrations';

export const APPOINTMENT_BOOK_TO_STATUS_MAPPING_TYPE =
  'appointmentBookToStatus';

export const APPOINTMENT_BOOK_TO_STATUS_MAPPING = CustomMapping.init({
  metadata: {
    label: 'Appointment Book to Status',
    description: `Used to map appointments from a specific appointment book to a specific appointment status. (eg All appointments in "Admin book" set to cancelled)`,
    type: APPOINTMENT_BOOK_TO_STATUS_MAPPING_TYPE,
    allowManualInput: true,
  },
  type: CustomMappingType.SelectionList,
  labelOverrides: {
    sourceIdentifier: 'Id',
    sourceLabel: 'Appointment Book',
    destinationIdentifier: 'Appointment Status',
    associatedValue: '',
  },
});

export enum AppointmentBookToAvailableStatus {
  Unscheduled = 'unscheduled',
  Cancelled = 'cancelled',
  Complete = 'complete',
  CancelWithFollowUp = 'cancelWithFollowUp',
  UnscheduledWithFollowUp = 'unscheduledWithFollowUp',
}

export const BOOK_STATUS_TO_APPOINTMENT_STATUS: Record<
  AppointmentBookToAvailableStatus,
  AppointmentStatus
> = {
  [AppointmentBookToAvailableStatus.Cancelled]: AppointmentStatus.Cancelled,
  [AppointmentBookToAvailableStatus.CancelWithFollowUp]:
    AppointmentStatus.Cancelled,
  [AppointmentBookToAvailableStatus.Unscheduled]: AppointmentStatus.Unscheduled,
  [AppointmentBookToAvailableStatus.UnscheduledWithFollowUp]:
    AppointmentStatus.Unscheduled,
  [AppointmentBookToAvailableStatus.Complete]: AppointmentStatus.Complete,
};

export interface IBaseAppointmentBook {
  id: number;
  description: string;
}

export abstract class AppointmentBookToStatusMappingHandler<
  AppointmentBook extends ISourceEntityTypes,
  SourceEntity extends BaseSourceEntity<AppointmentBook>,
> extends BaseCustomMappingHandler<
  AppointmentBook,
  AppointmentBookToAvailableStatus
> {
  customMapping = APPOINTMENT_BOOK_TO_STATUS_MAPPING;
  abstract sourceEntity: SourceEntity;

  abstract translateFn(record: AppointmentBook['data']): IBaseAppointmentBook;

  async getSourceOptions(
    migration: WithRef<IPracticeMigration>
  ): Promise<ICustomMappingSourceOption[]> {
    const records = await this.sourceEntity
      .getAllRecords$(migration)
      .toPromise();
    const options = records.map((record) => record.data);
    return sortBy(
      options.map((option) => {
        const translated = this.translateFn(option);
        return {
          label: translated.description,
          value: this.sourceEntity.getSourceRecordId(option).toString(),
        };
      }),
      'label'
    );
  }

  async getSelectionListOptions(
    _migration: WithRef<IPracticeMigration>
  ): Promise<CustomMappingOption[]> {
    return snapshot(
      of(
        getEnumValues(AppointmentBookToAvailableStatus).map((status) => ({
          value: status,
          description: status,
          hasAssociatedValue: false,
        }))
      )
    );
  }

  async getMappingBlob(
    migration: WithRef<IPracticeMigration>
  ): Promise<IBlobFilenamePair> {
    const { fileName, appointmentBooks, translator } =
      await this._getExporterData(migration);
    return new XSLXImporterExporter().getBlob(
      fileName,
      appointmentBooks,
      translator
    );
  }

  async downloadMapping(migration: WithRef<IPracticeMigration>): Promise<void> {
    const { fileName, appointmentBooks, translator } =
      await this._getExporterData(migration);
    await new XSLXImporterExporter().download(
      fileName,
      appointmentBooks,
      translator
    );
  }

  async uploadMapping(
    migration: WithRef<IPracticeMigration>,
    file: Blob
  ): Promise<void> {
    const items = await new XSLXImporterExporter().parse(
      file,
      new XSLXToAppointmentBookToStatus()
    );

    const translationMap = new TranslationMapHandler(migration);
    const records = await this.getRecords(translationMap);

    const failedRecords: IFailedCustomMappingRecord[] = [];

    await asyncForEach(records, (record) =>
      translationMap.deleteRecord(record)
    );

    const sourceOptions = await this.getSourceOptions(migration);
    const statusOptions = await this.getSelectionListOptions(migration);

    const recordsForUpload = items.map((item) => {
      const mapTo = item.mapTo;
      const id = item.sourceId;
      const label = item.sourceName;
      const value = sourceOptions.find(
        (sourceOption) => sourceOption.value === id
      )?.value;
      if (!value || !mapTo) {
        return;
      }

      const statusOption = statusOptions.find(
        (option) => option.value === mapTo
      );
      if (!statusOption) {
        // eslint-disable-next-line no-console
        console.error(
          `Mapping error: ${this.customMapping.metadata.label} - ${mapTo} not found`
        );

        failedRecords.push({
          sourceIdentifier: value,
          sourceLabel: label,
          reason: `Destination identifier "${mapTo?.trim()}" not found`,
        });
        return;
      }

      return {
        destinationValue: {
          value: statusOption.value as AppointmentBookToAvailableStatus,
        },
        sourceIdentifier: value,
        sourceLabel: label,
      };
    });

    await this.upsertBulk(compact(recordsForUpload), translationMap);

    const customMapping = await snapshot(
      PracticeMigration.findCustomMapping$(migration, this.customMapping)
    );
    if (!customMapping) {
      throw new Error('Custom mapping not found for updating failed records');
    }
    await Firestore.patchDoc(customMapping.ref, {
      failedRecords,
    });
  }

  private async _getExporterData(
    migration: WithRef<IPracticeMigration>
  ): Promise<{
    fileName: string;
    appointmentBooks: ICustomMappingSourceOption[];
    translator: AppointmentBookToStatusToXSLX;
  }> {
    const fileName = this.getFileName();
    const appointmentBooks = await this.getSourceOptions(migration);
    const translationMap = new TranslationMapHandler(migration);
    const translator = new AppointmentBookToStatusToXSLX(
      await this.getSelectionListOptions(migration),
      await this.getRecords(translationMap)
    );
    return {
      fileName,
      appointmentBooks,
      translator,
    };
  }
}
